import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled, { css } from 'styled-components';

import { Search, StudentsTable } from 'molecules';
import { MainTemplate } from 'templates';

import {
  useStudents,
  useStudentPermissions,
  usePopupStateOperations,
  useSearch,
  SearchNames,
} from 'core/hooks';
import { IRosterState } from 'components/molecules/StudentsTable/StudentsTableContainer';
import { PopupNames } from 'core';
import { getFontFamily } from 'services/FontService';
import { Colors } from 'services/ColorService';

export const Students = memo(() => {
  const { t } = useTranslation();
  const {
    data: students,
    loading,
    totalCount = 0,
    agreementsCount = 0,
    error,
    pageInfo,
    operations: { fetchMoreData },
  } = useStudents();
  const { canExportFinance } = useStudentPermissions();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const [rosterState, setRosterState] = useState<IRosterState>({
    filteredCount: 0,
    hasActiveFilter: false,
  });

  const showAddStudentPopup = useCallback(() => {
    showPopup(PopupNames.addStudent);
  }, [showPopup]);

  const { searchQuery, setSearchQuery } = useSearch(SearchNames.students);

  const onChangeHandler = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery],
  );

  const onClearSearchHandler = useCallback(() => {
    setSearchQuery('');
    fetchMoreData({ query: null });
  }, [setSearchQuery, fetchMoreData]);

  const fetchMore = useCallback(() => {
    const vars = {
      query: searchQuery || null,
      after: pageInfo?.endCursor,
    };

    fetchMoreData(vars);
  }, [fetchMoreData, pageInfo?.endCursor, searchQuery]);

  useEffect(() => {
    const trimedQuery = searchQuery.trim();
    const query = trimedQuery || null;

    fetchMoreData({ query });
  }, [searchQuery, fetchMoreData]);

  // Eager-load to completion while a filter is active so filtering and the
  // filtered count cover the WHOLE search result, not just loaded pages
  // (Req 3.10, 4.1). Stops once `hasNextPage` is false.
  useEffect(() => {
    if (rosterState.hasActiveFilter && pageInfo?.hasNextPage && !loading) {
      fetchMoreData({
        query: searchQuery || null,
        after: pageInfo.endCursor,
      });
    }
  }, [
    rosterState.hasActiveFilter,
    pageInfo?.hasNextPage,
    pageInfo?.endCursor,
    loading,
    searchQuery,
    fetchMoreData,
  ]);

  // While a filter is active the count reflects the filtered set, but only once
  // the roster is fully loaded — otherwise a localized placeholder so a partial
  // number is never presented as final (Req 4.3, 4.4).
  const rosterStillLoading = Boolean(pageInfo?.hasNextPage);
  const displayCount = rosterState.hasActiveFilter
    ? rosterStillLoading
      ? t('pages.students.countLoading')
      : String(rosterState.filteredCount)
    : String(totalCount);

  return (
    <MainTemplate>
      <StudentsWrapper>
        <HeadingWrapper>
          <Heading>{t('pages.students.students')}</Heading>
          <Counter>{displayCount}</Counter>

          <Heading>{t('pages.students.agreements')}</Heading>
          <Counter>{String(agreementsCount)}</Counter>
        </HeadingWrapper>
        <SearchWrapper>
          <Search
            value={searchQuery}
            onChange={onChangeHandler}
            clear={onClearSearchHandler}
            placeholder={t('pages.students.searchStudent')}
            buttonProps={{
              label: `${t('pages.students.addStudent')}`,
              onClick: showAddStudentPopup,
            }}
          />
        </SearchWrapper>
        <TableWrapper>
          <StudentsTable
            data={students}
            loading={loading}
            error={error}
            hasMore={pageInfo?.hasNextPage || false}
            loadMore={fetchMore}
            canExportFinance={canExportFinance}
            onRosterStateChange={setRosterState}
          />
        </TableWrapper>
      </StudentsWrapper>
    </MainTemplate>
  );
});

const StudentsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  box-sizing: border-box;
`;

const baseWrapperStyles = css`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const HeadingWrapper = styled.div`
  margin-top: 48px;
`;

const Heading = styled.span`
  font-family: ${getFontFamily('bold')};
  font-size: 20px;
  line-height: 40px;
  margin-right: 16px;
`;

const Counter = styled.span`
  color: ${Colors.Blue};
  font-family: ${getFontFamily('medium')};
  font-size: 20px;
  line-height: 32px;
  margin-right: 64px;
`;

const SearchWrapper = styled.div`
  ${baseWrapperStyles};
  margin-top: 16px;
`;
const TableWrapper = styled.div`
  ${baseWrapperStyles};
  margin-top: 36px;
`;
